import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDebouncedEffect } from '@ozgene/gp5.hooks.use-debounced-effect';
import { CustomAutocomplete } from '@ozgene/gp5.components.custom-autocomplete';

export function SelectEntity(props) {
  const {
    value,
    selectDefault,
    selectValue,
    onChange,
    getEntities,
    label,
    getOptionLabel,
    getOptionSelected,
    queryOnSearch,
    multiple,
    initialSearchText,
    useEntities,
    filterEntities,
    CreateEntityDialogComponent,
    createEntityInitialData,
    createEntityReferenceData,
    createEntityNameProperty,
    onCreateEntitySave,
    minCharacters,
    error,
    errorText,
    ...restProps
  } = props;
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [entityValue, setEntityValue] = useState(multiple ? [] : null);
  const [inputValue, setInputValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [newEntityText, setNewEntityText] = useState('');
  const [entitiesCreated, setEntitiesCreated] = useState(0);
  const [createdEntitiesLoaded, setCreatedEntitiesLoaded] = useState(0);
  const [createdEntity, setCreatedEntity] = useState();
  const [createEntityDialogOpen, setCreateEntityDialogOpen] = useState(false);
  const handleAddValue = useMemo(
    () =>
      CreateEntityDialogComponent
        ? (value) => {
            setNewEntityText(value);
            setCreateEntityDialogOpen(true);
          }
        : null,
    [CreateEntityDialogComponent]
  );
  const handleInputChange = (_event, value) => setInputValue(value);
  const handleCloseEntityDialog = () => {
    setInputValue('');
    setCreateEntityDialogOpen(false);
  };

  const handleSaveEntity = async (result) => {
    const saveResult = await onCreateEntitySave(result);

    setCreatedEntity(multiple ? [...value, saveResult] : saveResult);
    setEntitiesCreated(entitiesCreated + 1);
  };

  useEffect(() => setInputValue(initialSearchText), [initialSearchText]);
  useDebouncedEffect(() => setSearchText(inputValue), [inputValue], 500);
  useEffect(() => {
    if (
      (value === null || (multiple && value.length === 0)) &&
      selectDefault &&
      !selectValue &&
      entities.length > 0
    ) {
      const defaultEntity = entities.find(selectDefault);

      if (defaultEntity) {
        setEntityValue(multiple ? [defaultEntity] : defaultEntity);
        onChange(multiple ? [defaultEntity] : defaultEntity);
      }
    }
  }, [value, selectDefault, multiple, entities, onChange, selectValue]);

  useEffect(() => {
    setEntityValue(value);
  }, [value]);

  useEffect(() => {
    if (selectValue !== undefined && entities.length > 0) {
      if (selectValue !== null) {
        const selectedEntity = entities.find(selectValue);

        if (selectedEntity) {
          setEntityValue(multiple ? [selectedEntity] : selectedEntity);
        } else {
          setEntityValue(multiple ? [] : null);
        }
      } else {
        setEntityValue(multiple ? [] : null);
      }
    }
  }, [selectValue, multiple, entities]);

  useEffect(() => {
    if (useEntities) {
      setEntities(useEntities);
      setCreatedEntitiesLoaded(entitiesCreated);
    }
  }, [useEntities, entitiesCreated]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setEntities(await getEntities());
      setCreatedEntitiesLoaded(entitiesCreated);
      setLoading(false);
    };

    if (!useEntities && !queryOnSearch) {
      loadData();
    }
  }, [useEntities, getEntities, queryOnSearch, entitiesCreated]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setEntities(searchText.length > 0 ? await getEntities(searchText) : []);
      setCreatedEntitiesLoaded(entitiesCreated);
      setLoading(false);
    };
    if (queryOnSearch && !useEntities) {
      loadData();
    }
  }, [useEntities, queryOnSearch, searchText, getEntities, entitiesCreated]);

  const filteredEntities = useMemo(
    () => (filterEntities ? entities.filter(filterEntities) : entities),
    [entities, filterEntities]
  );

  useEffect(() => {
    if (entitiesCreated > 0 && entitiesCreated === createdEntitiesLoaded) {
      onChange(createdEntity);
    }
  }, [createdEntitiesLoaded, createdEntity, entitiesCreated]);

  return (
    <>
      <CustomAutocomplete
        loading={loading}
        multiple={multiple}
        options={filteredEntities}
        value={entityValue}
        onChange={onChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        label={label}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected}
        onAddValue={handleAddValue}
        error={error}
        errorText={errorText}
        {...restProps}
      />
      {CreateEntityDialogComponent && createEntityDialogOpen && (
        <CreateEntityDialogComponent
          onSave={handleSaveEntity}
          initialData={createEntityInitialData}
          referenceData={createEntityReferenceData}
          onClose={handleCloseEntityDialog}
          defaultText={newEntityText}
        />
      )}
    </>
  );
}

SelectEntity.propTypes = {
  /** The selected entity/entities */
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      })
    ),
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ]),
  /** Minimum number of characters required to display options */
  minCharacters: PropTypes.number,
  /** Default selection */
  selectDefault: PropTypes.func,
  /** Select value by ID or other mechanism */
  selectValue: PropTypes.func,
  /** Called when the user selects a new item */
  onChange: PropTypes.func.isRequired,
  /** Called when setting up the seletion entity list */
  getEntities: PropTypes.func.isRequired,
  /** Text displayed in the selection space */
  label: PropTypes.string,
  /** Converts the selection objects into a readable string to display in the list */
  getOptionLabel: PropTypes.func,
  /** Does something with the selected object i guess */
  getOptionSelected: PropTypes.func,
  /** Adds to the query when typing in the text field */
  queryOnSearch: PropTypes.bool,
  /** Defines whether the user can select multiple entities at onece */
  multiple: PropTypes.bool,
  /** A string applied by default to the search field */
  initialSearchText: PropTypes.string,
  useEntities: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired })
  ),
  /** Filtering function */
  filterEntities: PropTypes.func,
  /** Defines the dialog component that opens when the user creates a new entity */
  CreateEntityDialogComponent: PropTypes.func,
  /** Initial data for creating a new entity */
  // eslint-disable-next-line react/forbid-prop-types
  createEntityInitialData: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  createEntityReferenceData: PropTypes.object,
  createEntityNameProperty: PropTypes.string,
  /** Saves the newly created entity */
  onCreateEntitySave: PropTypes.func,
  /** defines when its in an error state */
  error: PropTypes.bool,
  /** Text to display when in an error state */
  errorText: PropTypes.string,
};

SelectEntity.defaultProps = {
  value: undefined,
  selectDefault: null,
  selectValue: undefined,
  label: 'Entity',
  queryOnSearch: false,
  multiple: false,
  minCharacters: 0,
  initialSearchText: '',
  useEntities: null,
  filterEntities: null,
  getEntities: () => [],
  getOptionLabel: (option) => option.name,
  getOptionSelected: (option, value) => option.id === value.id,
  CreateEntityDialogComponent: null,
  createEntityInitialData: null,
  createEntityReferenceData: null,
  onCreateEntitySave: null,
  error: false,
  errorText: '',
};
